import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trip, TripElement, FlightDetails } from "@/types/trip";
import { format, parseISO, isAfter, isBefore, addHours } from "date-fns";
import { identifyRealDestination, isValidUUID } from "@/utils/tripUtils";
import Hey from "@/lib/hey-utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCityFromAirportCode } from "@/utils/airportMapping";

export const useBasicTripCardData = (trip: Trip, isFocused = false) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isMobile = useIsMobile();

  console.debug('================================================');
  console.debug('🎯 TRIP ID:', trip.id);
  console.debug('🔍 Platform:', isMobile ? 'Mobile' : 'Desktop');
  console.debug('================================================');

  if (!trip.elements || trip.elements.length === 0) {
    console.debug('⚠️ Trip has no elements:', trip.id);
    setIsLoading(false);
    return {
      trip,
      isFocused,
      sortedElements: [],
      isPastTrip: false,
      nextSegment: null,
      firstFlight: null,
      isFutureTrip: false,
      mainDestinationCode: '',
      isLoading: false,
      isDeleting,
      setIsDeleting,
      isDrawerOpen,
      setIsDrawerOpen,
      hotelData: [],
      getDestinationImage: () => trip.destinationImage || '/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png',
      getDestinationName: () => trip.identifiedDestination || trip.destination || 'Unknown Destination',
      getAirportInfo: () => ({
        imageUrl: '',
        airportName: "",
        cityName: "",
        airportCode: "",
        countryName: '',
        cityCode: "",
        countryCode: ''
      }),
      canCheckIn: () => false,
      handleButtonClick: (e: React.MouseEvent) => {
        e.stopPropagation();
      }
    };
  }

  const sortedElements = trip.elements?.slice()?.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()) || [];
  const now = new Date();
  const isPastTrip = sortedElements.length > 0 && sortedElements.every(element => {
    const endDate = parseISO(element.endDate  || "1970");
    return isBefore(endDate, now);
  });

  const getNextRelevantSegment = (): TripElement | null => {
    if (isPastTrip || sortedElements.length === 0) return null;
    const ongoingSegments = sortedElements.filter(element => {
      const startDate = parseISO(element.startDate  || "1970");
      const endDate = parseISO(element.endDate  || "1970");
      return isAfter(now, startDate) && isBefore(now, endDate);
    });
    if (ongoingSegments.length > 0) {
      return ongoingSegments.sort((a, b) => parseISO(a.endDate || "1970").getTime() - parseISO(b.endDate || "1970").getTime())[0];
    }
    const upcomingSegments = sortedElements.filter(element => {
      const startDate = parseISO(element.startDate || "1970");
      return isAfter(startDate, now);
    });
    if (upcomingSegments.length > 0) {
      return upcomingSegments.sort((a, b) => parseISO(a.startDate || "1970").getTime() - parseISO(b.startDate || "1970").getTime())[0];
    }
    return null;
  };

  const nextSegment = getNextRelevantSegment();
  const firstFlight = sortedElements.find(element => element.type === 'flight');
  const isFutureTrip = firstFlight ? new Date(firstFlight.startDate) > new Date() : false;
  const mainDestinationCode = identifyRealDestination(sortedElements);
  console.debug('🕒 First flight date:', firstFlight?.startDate);
  console.debug('🔮 Is future trip?', isFutureTrip);
  console.debug('🧠 Identified main destination code:', mainDestinationCode);

  const {
    data: hotelData
  } = useQuery({
    queryKey: ['hotel', trip.id],
    queryFn: async () => {
      if (!trip.id) return null;
      let hotelsFromTrip = trip.elements?.filter(element => element.type === 'hotel');
      return hotelsFromTrip;
    },
    enabled: isValidUUID(trip.id)
  });

  const {
    data: _iata_infos_map = []
  } = useQuery({
    queryKey: ['_iata_infos_map', trip.id],
    queryFn: async () => {
      console.debug('🔄 Starting IATA info fetch for trip:', trip.id, 'on', isMobile ? 'Mobile' : 'Desktop');
      let iata_map = {};
      for (let element of trip.elements) {
        if (element.type === 'flight') {
          let flight = element.details as FlightDetails;
          console.debug('Fetching IATA infos for:', flight.departure.code, flight.arrival.code);
          if (flight.departure?.code) {
            try {
              iata_map[`IATA_${flight.departure.code}`] = await Hey.HeyUtils.getInstance().getAirportInfos(flight.departure.code, Hey.ImageFormat.CUSTOM2);
            } catch (error) {
              console.error(`Failed to fetch info for airport ${flight.departure.code}:`, error);
            }
          }
          if (flight.arrival?.code) {
            try {
              iata_map[`IATA_${flight.arrival.code}`] = await Hey.HeyUtils.getInstance().getAirportInfos(flight.arrival.code, Hey.ImageFormat.CUSTOM2);
            } catch (error) {
              console.error(`Failed to fetch info for airport ${flight.arrival.code}:`, error);
            }
          }
        }
      }
      console.debug('✅ Completed IATA info fetch for trip:', trip.id);
      setIsLoading(false);
      return iata_map;
    }
  });

  const defaultDestinationImage = "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";

  const getDestinationImage = () => {
    console.debug('🖼️ getDestinationImage called for trip:', {
      tripId: trip.id,
      platform: isMobile ? 'Mobile' : 'Desktop',
      existingImage: trip.destinationImage
    });
    
    // if (trip.destinationImage) {
    //   console.debug('🖼️ Using existing destination image from trip:', {
    //     tripId: trip.id,
    //     platform: isMobile ? 'Mobile' : 'Desktop',
    //     image: trip.destinationImage
    //   });
    //   return trip.destinationImage;
    // }
    
    if (!mainDestinationCode || !_iata_infos_map) {
      console.debug('🖼️ getDestinationImage | No mainDestinationCode or _iata_infos_map, using default for:', {
        tripId: trip.id,
        platform: isMobile ? 'Mobile' : 'Desktop',
        mainDestinationCode,
        hasIataInfosMap: !!_iata_infos_map
      });
      return defaultDestinationImage;
    }

    const iataKey = `IATA_${mainDestinationCode}`;
    const airportInfo = _iata_infos_map[iataKey];
    
    console.debug('🖼️ getDestinationImage | Looking up airport info for image:', {
      tripId: trip.id,
      platform: isMobile ? 'Mobile' : 'Desktop',
      mainDestinationCode,
      iataKey,
      airportInfoExists: !!airportInfo,
      imageUrl: airportInfo?.imageUrl
    });
    
    if (!airportInfo || !airportInfo.imageUrl || airportInfo.imageUrl === '') {
      console.debug('🖼️ getDestinationImage | No valid image found, using default for:', {
        tripId: trip.id,
        platform: isMobile ? 'Mobile' : 'Desktop',
        reason: !airportInfo ? 'No airport info' : 'No image URL'
      });
      return defaultDestinationImage;
    }
    
    console.debug('🖼️ getDestinationImage | Using airport info image for:', {
      tripId: trip.id,
      platform: isMobile ? 'Mobile' : 'Desktop',
      mainDestinationCode,
      imageUrl: airportInfo.imageUrl
    });
    if (airportInfo.imageUrl) {
      trip.destinationImage = airportInfo.imageUrl;
      console.debug('🖼️ getDestinationImage | Updated trip destination image:')
    }
    return airportInfo.imageUrl || defaultDestinationImage;
  };

  const capitalizeWords = (str: string): string => {
    return str.replace(/\b\w+/g, word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
  };

  const getCachedAirportInfo = (airportCode: string) => {
    const cachedData = localStorage.getItem(`airportInfosCache_${airportCode}`);
    if (cachedData) {
      try {
        const parsedCache = JSON.parse(cachedData);
        console.debug(`📋 Using cached airport info for ${airportCode}:`, parsedCache);
        return parsedCache;
      } catch (error) {
        console.error(`Failed to parse cached airport info for ${airportCode}:`, error);
      }
    }
    console.debug(`🔎 No cached info found for ${airportCode}`);
    return null;
  };

  const getDestinationName = () => {
    console.debug(`🧭 getDestinationName called for trip ${trip.id} on ${isMobile ? 'Mobile' : 'Desktop'}`);
    
    const flightSegments = trip.elements.filter(element => element.type === 'flight');
    const hotelSegments = trip.elements.filter(element => element.type === 'hotel');
    
    if (flightSegments.length === 0 && hotelSegments.length > 0) {
      const firstHotel = hotelSegments[0];
      const hotelName = (firstHotel.details as any).name || 'Hotel Stay';
      console.debug(`🏨 Using hotel name: ${hotelName}`);
      return capitalizeWords(hotelName);
    }
    
    if (!mainDestinationCode) {
      console.debug(`⚠️ No mainDestinationCode available, falling back to trip.destination: ${trip.destination}`);
      return trip.destination || 'Unknown Destination';
    }
    
    const airportInfo = _iata_infos_map?.[`IATA_${mainDestinationCode}`];
    
    let cityName = airportInfo?.cityName;
    
    console.debug(`🏙️ Airport info from API for ${mainDestinationCode}:`, airportInfo ? {
      cityName: airportInfo.cityName,
      airportName: airportInfo.airportName,
      hasValidCityName: cityName && cityName !== "Unknown"
    } : 'Not available');
    
    if (!cityName || cityName === "Unknown") {
      const cachedInfo = getCachedAirportInfo(mainDestinationCode);
      cityName = cachedInfo?.cityName;
      console.debug(`🏙️ City name from cache: ${cityName || 'Not available'}`);
    }
    
    console.debug("🏙️ getDestinationName DEBUG:", {
      tripId: trip.id,
      mainDestinationCode,
      mappedCityName: getCityFromAirportCode(mainDestinationCode),
      airportInfoCityName: cityName,
      tripDestination: trip.destination,
      identifiedDestination: trip.identifiedDestination,
      isMobile,
      hasValidCityName: cityName && cityName !== "Unknown",
      hasValidMappedCity: getCityFromAirportCode(mainDestinationCode) !== "Unknown City",
      finalReturnedDestination: cityName || getCityFromAirportCode(mainDestinationCode) || trip.destination || mainDestinationCode
    });
    
    if (cityName && cityName !== "Unknown") {
      console.debug(`✅ Returning city name from API/cache: ${cityName}`);
      return cityName;
    }
    
    const mappedCityName = getCityFromAirportCode(mainDestinationCode);
    if (mappedCityName && mappedCityName !== "Unknown City") {
      console.debug(`✅ Returning mapped city name: ${mappedCityName}`);
      return mappedCityName;
    }
    
    if (trip.destination && trip.destination !== "Unknown Destination") {
      console.debug(`✅ Returning trip.destination: ${trip.destination}`);
      return trip.destination;
    }
    
    console.debug(`✅ Returning fallback: ${mainDestinationCode}`);
    return mainDestinationCode;
  };

  const getAirportInfo = (flightElement: any, type: 'departure' | 'arrival') => {
    const iataCode = flightElement?.details?.[type]?.code;
    if (_iata_infos_map[`IATA_${iataCode}`]) {
      return _iata_infos_map[`IATA_${iataCode}`];
    }
    const otherType = type === 'departure' ? 'arrival' : 'departure';
    const otherIataCode = flightElement?.details?.[otherType]?.code;
    if (_iata_infos_map[`IATA_${otherIataCode}`]) {
      return _iata_infos_map[`IATA_${otherIataCode}`];
    }
    console.error('No airport location found for:', flightElement?.details?.[type]?.code, _iata_infos_map, flightElement, "Trip:", trip);
    return {
      imageUrl: '',
      airportName: "",
      cityName: "",
      airportCode: flightElement?.details?.[type]?.code || "",
      countryName: '',
      cityCode: flightElement?.details?.[type]?.code || "",
      countryCode: ''
    };
  };

  const canCheckIn = () => {
    const firstFlight = sortedElements.find(element => element.type === 'flight');
    if (!firstFlight) return false;
    let departureTime;
    try {
      departureTime = parseISO((firstFlight.details as FlightDetails).departure.time || "1970");
    } catch (error) {
      console.error('Error parsing departure time:', error);
      return false;
    }
    const now = new Date();
    const fortyEightHoursFromNow = addHours(now, 48);
    return isAfter(departureTime, now) && isBefore(departureTime, fortyEightHoursFromNow);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return {
    trip,
    isFocused,
    sortedElements,
    isPastTrip,
    nextSegment,
    firstFlight,
    isFutureTrip,
    mainDestinationCode,
    isLoading,
    isDeleting,
    setIsDeleting,
    isDrawerOpen,
    setIsDrawerOpen,
    hotelData,
    getDestinationImage,
    getDestinationName,
    getAirportInfo,
    canCheckIn,
    handleButtonClick
  };
};
